<template>
    <span v-if="activeThreshold" :class="['date-age-badge', activeThreshold.className]">
        {{ activeThreshold.label }}
    </span>
</template>

<script>
    const MS_PER_DAY = 86400000

    export default {
        name: 'DateAgeBadge',
        props: {
            date: {
                type: [String, Number, Date],
                default: null,
            },
            thresholds: {
                type: Array,
                default: () => [
                    { days: 90, className: 'date-age-badge--danger' },
                    { days: 60, className: 'date-age-badge--warning-high' },
                    { days: 30, className: 'date-age-badge--warning' },
                ],
            },
        },
        computed: {
            activeThreshold() {
                const daysOld = this.daysOld
                if (daysOld == null) {
                    return null
                }

                const threshold = this.normalizedThresholds.find(({ days }) => daysOld > days)
                if (!threshold) {
                    return null
                }

                return {
                    ...threshold,
                    label: threshold.label || `> ${threshold.days} days old`,
                }
            },
            daysOld() {
                if (!this.date) {
                    return null
                }

                const date = this.date instanceof Date ? this.date : new Date(this.date)
                if (Number.isNaN(date.getTime())) {
                    return null
                }

                const today = new Date()
                const todayUtc = Date.UTC(
                    today.getUTCFullYear(),
                    today.getUTCMonth(),
                    today.getUTCDate()
                )
                const dateUtc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())

                return Math.floor((todayUtc - dateUtc) / MS_PER_DAY)
            },
            normalizedThresholds() {
                return this.thresholds
                    .map((threshold) => ({
                        ...threshold,
                        days: Number(threshold?.days),
                    }))
                    .filter((threshold) => Number.isFinite(threshold.days) && threshold.className)
                    .sort((a, b) => b.days - a.days)
            },
        },
    }
</script>

<style scoped>
    .date-age-badge {
        border: 1px solid transparent;
        border-radius: var(--radius-sm);
        display: inline-flex;
        font-size: 0.72rem;
        font-weight: 700;
        line-height: 1.2;
        padding: 0.125rem 0.375rem;
        white-space: nowrap;
    }

    .date-age-badge--warning {
        background: var(--color-date-alert-30-bg);
        border-color: var(--color-date-alert-30-border);
        color: var(--color-date-alert-30-text);
    }

    .date-age-badge--warning-high {
        background: var(--color-date-alert-60-bg);
        border-color: var(--color-date-alert-60-border);
        color: var(--color-date-alert-60-text);
    }

    .date-age-badge--danger {
        background: var(--color-date-alert-90-bg);
        border-color: var(--color-date-alert-90-border);
        color: var(--color-date-alert-90-text);
    }
</style>
